// Centralised image resolution + per-type fallback chain.
// Used by all card and detail components to guarantee an image is always shown.
//
// Each content type has a *pool* of 20+ curated editorial images. The image
// shown for a given entity is picked deterministically from the pool using a
// hash of the entity id, so:
//   - the same entry always shows the same image (no flicker between renders);
//   - different entries on the same page show *different* generic images.

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const u = (id) => `https://images.unsplash.com/photo-${id}?w=1400&q=80&auto=format&fit=crop`;

// Per-type curated pools (20+ images each). Themes:
//   news      -> editorial / newsroom / business reading
//   insight   -> analysis, charts, deep-dive, research
//   alert     -> monitoring, signals, screens, dashboards
//   event     -> conferences, meetings, panels, audiences
//   job       -> hiring, teams at work, modern offices
//   tutorial  -> learning, code, workshop, hands-on
//   ecosystem -> partnerships, networking, business community
export const FALLBACK_POOLS = {
  news: [
    u("1495020689067-958852a7765e"),
    u("1504711434969-e33886168f5c"),
    u("1585829365295-ab7cd400c167"),
    u("1586892477838-2b96e85e0f96"),
    u("1576091160550-2173dba999ef"),
    u("1518186285589-2f7649de83e0"),
    u("1454165804606-c3d57bc86b40"),
    u("1432888622747-4eb9a8efeb07"),
    u("1497032628192-86f99bcd76bc"),
    u("1486312338219-ce68d2c6f44d"),
    u("1542435503-956c469947f6"),
    u("1495465798138-718f86d1a4bc"),
    u("1542744173-8e7e53415bb0"),
    u("1551434678-e076c223a692"),
    u("1499750310107-5fef28a66643"),
    u("1551836022-d5d88e9218df"),
    u("1543286386-2e659306cd6c"),
    u("1521737711867-e3b97375f902"),
    u("1556761175-5973dc0f32e7"),
    u("1450101499163-c8848c66ca85"),
  ],
  insight: [
    u("1454165804606-c3d57bc86b40"),
    u("1543286386-2e659306cd6c"),
    u("1518186285589-2f7649de83e0"),
    u("1460925895917-afdab827c52f"),
    u("1551288049-bebda4e38f71"),
    u("1486312338219-ce68d2c6f44d"),
    u("1542744173-8e7e53415bb0"),
    u("1495465798138-718f86d1a4bc"),
    u("1551434678-e076c223a692"),
    u("1499750310107-5fef28a66643"),
    u("1576091160550-2173dba999ef"),
    u("1542626991-cbc4e32524cc"),
    u("1497032628192-86f99bcd76bc"),
    u("1432888622747-4eb9a8efeb07"),
    u("1542435503-956c469947f6"),
    u("1450101499163-c8848c66ca85"),
    u("1551836022-d5d88e9218df"),
    u("1556761175-5973dc0f32e7"),
    u("1494178270175-e96de2971df9"),
    u("1554224155-8d04cb21cd6c"),
  ],
  alert: [
    u("1611974789855-9c2a0a7236a3"),
    u("1605792657660-596af9009e82"),
    u("1581090464777-f3220bbe1b8b"),
    u("1518770660439-4636190af475"),
    u("1485827404703-89b55fcc595e"),
    u("1496247749665-49cf5b1022e9"),
    u("1485965120184-e220f721d03e"),
    u("1554224155-8d04cb21cd6c"),
    u("1531297484001-80022131f5a1"),
    u("1542744094-3a31f272c490"),
    u("1551434678-e076c223a692"),
    u("1499750310107-5fef28a66643"),
    u("1576091160550-2173dba999ef"),
    u("1543286386-2e659306cd6c"),
    u("1486312338219-ce68d2c6f44d"),
    u("1495465798138-718f86d1a4bc"),
    u("1542744173-8e7e53415bb0"),
    u("1454165804606-c3d57bc86b40"),
    u("1518186285589-2f7649de83e0"),
    u("1551836022-d5d88e9218df"),
  ],
  event: [
    u("1556761175-5973dc0f32e7"),
    u("1505373877841-8d25f7d46678"),
    u("1540575467063-178a50c2df87"),
    u("1511795409834-ef04bbd61622"),
    u("1517048676732-d65bc937f952"),
    u("1559223607-a43c990c692c"),
    u("1591115765373-5207764f72e7"),
    u("1531058020387-3be344556be6"),
    u("1492684223066-81342ee5ff30"),
    u("1475721027785-f74eccf877e2"),
    u("1576091160399-112ba8d25d1d"),
    u("1542744095-fcf48d80b0fd"),
    u("1540304453527-62f979142a17"),
    u("1591115765373-5207764f72e7"),
    u("1517245386807-bb43f82c33c4"),
    u("1543269865-cbf427effbad"),
    u("1551836022-d5d88e9218df"),
    u("1518186285589-2f7649de83e0"),
    u("1431540015161-0bf868a2d407"),
    u("1530099486328-e021101a494a"),
  ],
  job: [
    u("1521737711867-e3b97375f902"),
    u("1521737604893-d14cc237f11d"),
    u("1573496359142-b8d87734a5a2"),
    u("1556761175-5973dc0f32e7"),
    u("1543269865-cbf427effbad"),
    u("1497032628192-86f99bcd76bc"),
    u("1542744095-fcf48d80b0fd"),
    u("1556761175-5973dc0f32e7"),
    u("1551836022-d5d88e9218df"),
    u("1542744094-3a31f272c490"),
    u("1517245386807-bb43f82c33c4"),
    u("1543269865-cbf427effbad"),
    u("1531058020387-3be344556be6"),
    u("1486312338219-ce68d2c6f44d"),
    u("1497215842964-222b430dc094"),
    u("1542744173-8e7e53415bb0"),
    u("1542435503-956c469947f6"),
    u("1454165804606-c3d57bc86b40"),
    u("1542744095-fcf48d80b0fd"),
    u("1559223607-a43c990c692c"),
  ],
  tutorial: [
    u("1454165804606-c3d57bc86b40"),
    u("1517694712202-14dd9538aa97"),
    u("1531403009284-440f080d1e12"),
    u("1542831371-29b0f74f9713"),
    u("1556761175-5973dc0f32e7"),
    u("1551434678-e076c223a692"),
    u("1542744173-8e7e53415bb0"),
    u("1486312338219-ce68d2c6f44d"),
    u("1518186285589-2f7649de83e0"),
    u("1497032628192-86f99bcd76bc"),
    u("1499750310107-5fef28a66643"),
    u("1543286386-2e659306cd6c"),
    u("1576091160550-2173dba999ef"),
    u("1495465798138-718f86d1a4bc"),
    u("1542435503-956c469947f6"),
    u("1551836022-d5d88e9218df"),
    u("1450101499163-c8848c66ca85"),
    u("1503676260728-1c00da094a0b"),
    u("1531403009284-440f080d1e12"),
    u("1517048676732-d65bc937f952"),
  ],
  ecosystem: [
    u("1554469384-e58fac16e23a"),
    u("1521737711867-e3b97375f902"),
    u("1556761175-5973dc0f32e7"),
    u("1542744095-fcf48d80b0fd"),
    u("1573496359142-b8d87734a5a2"),
    u("1497215842964-222b430dc094"),
    u("1551434678-e076c223a692"),
    u("1521737604893-d14cc237f11d"),
    u("1431540015161-0bf868a2d407"),
    u("1559223607-a43c990c692c"),
    u("1531058020387-3be344556be6"),
    u("1543269865-cbf427effbad"),
    u("1542744094-3a31f272c490"),
    u("1517245386807-bb43f82c33c4"),
    u("1454165804606-c3d57bc86b40"),
    u("1486312338219-ce68d2c6f44d"),
    u("1497032628192-86f99bcd76bc"),
    u("1576091160550-2173dba999ef"),
    u("1551836022-d5d88e9218df"),
    u("1556761175-5973dc0f32e7"),
  ],
};

// Global last-resort fallback (Dataleo neutral image).
export const GLOBAL_FALLBACK = u("1494412519320-aa613dfb7738");

// Deterministic hash → integer ≥ 0. Same input always returns same output.
const hashSeed = (s) => {
  const str = String(s || "");
  let h = 5381;
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) + h) + str.charCodeAt(i); // h * 33 + c
    h |= 0;
  }
  return Math.abs(h);
};

// Pick one image from the type pool, deterministically based on `seed`.
// Without a seed, returns the first image of the pool (legacy behaviour).
export const fallbackForType = (type = "news", seed = "") => {
  const pool = FALLBACK_POOLS[type] || FALLBACK_POOLS.news;
  if (!pool || pool.length === 0) return GLOBAL_FALLBACK;
  if (!seed) return pool[0];
  return pool[hashSeed(seed) % pool.length];
};

// Back-compat: legacy callers that imported FALLBACK_BY_TYPE as a string map
// (one image per type) keep working. Each value here is just the pool's first
// entry; entity-aware callers should switch to `fallbackForType(type, id)` or
// `pickEntityImage(entity, type)`.
export const FALLBACK_BY_TYPE = Object.fromEntries(
  Object.entries(FALLBACK_POOLS).map(([k, v]) => [k, v[0]])
);

// Normalize a stored URL: `/api/uploads/...` → absolute backend URL.
export const resolveImg = (url) => {
  if (!url || typeof url !== "string") return "";
  const trimmed = url.trim();
  if (!trimmed) return "";
  if (trimmed.startsWith("/api/")) return `${BACKEND}${trimmed}`;
  if (trimmed.startsWith("//")) return `https:${trimmed}`;
  return trimmed;
};

// Pick the primary image for an entity following the priority chain:
//   1. entity's own image field (image_url / featured_image / company_logo / logo_url)
//   2. first linked ecosystem entity logo (if provided via `ecosystemLogo`)
//   3. type-specific fallback pool, seeded by entity.id / slug
//   4. global fallback
export const pickEntityImage = (entity = {}, type = "news", opts = {}) => {
  const own =
    entity.image_url ||
    entity.featured_image ||
    entity.company_logo ||
    entity.logo_url ||
    "";
  const resolved = resolveImg(own);
  if (resolved) return resolved;
  if (opts.ecosystemLogo) {
    const eco = resolveImg(opts.ecosystemLogo);
    if (eco) return eco;
  }
  const seed = entity.id || entity.slug || entity.title || "";
  return fallbackForType(type, seed);
};

// onError handler factory. Falls back type-fallback then global-fallback.
// Uses a data attribute on the element to prevent infinite loops.
// `seed` (optional) keeps fallbacks deterministic per entity.
export const makeImgErrorHandler = (type = "news", seed = "") => (e) => {
  const img = e.currentTarget;
  const stage = img.dataset.fallbackStage || "0";
  if (stage === "0") {
    img.dataset.fallbackStage = "1";
    img.src = fallbackForType(type, seed);
  } else if (stage === "1") {
    img.dataset.fallbackStage = "2";
    img.src = GLOBAL_FALLBACK;
  } else {
    // Final stage: hide the image, leave container background visible.
    img.style.visibility = "hidden";
  }
};
